import { Typography } from "@mui/material"
import { Fragment } from "react"
import { decideDataTypeMaskingKind, getNumericTypeStorageLength } from "~/components/governance/fieldEdit/maskingRuleDataTypesMap"
import { FieldResponseDto, ObfuscationDataType } from "~/gen/api/encrypt"

export interface InferredDate {
  type: 'date',
  length: number,
}

export interface InferredString {
  type: 'string',
  length: number,
}

export interface InferredInteger {
  type: 'integer',
  length: number,
}

export interface InferredNumeric {
  type: 'numeric',
  p: number,
  s: number,
  pn: number,
  pOra: number,
  sOra: number,
}

export type Inferred = InferredString | InferredInteger | InferredNumeric | InferredDate

export interface InferredDataType {
  type: string,
  length?: number,
  p?: number,
  s?: number,
  pn?: number,
  pOra?: number,
  sOra?: number,
}

const dateKinds = {
  'smalldatetime': { min: new Date('1900-01-01'), max: new Date('2079-06-06'), rank: 1 },
  'datetime':      { min: new Date('1753-01-01'), max: new Date('9999-12-31'), rank: 2 },
}
type dateKindsKey = keyof(typeof dateKinds)
export const figureOutDateRank = (x:string) => {
  if (!x) { return 3 }
  const low_x = x.toLocaleLowerCase()
  return dateKinds[low_x as dateKindsKey] ? dateKinds[low_x as dateKindsKey].rank : 3
}

export const extractFromField = (x: FieldResponseDto): { inferredType: Inferred|undefined, dataType: ObfuscationDataType }|undefined => {
  if (!x.type) { return }
  const srcColumnType = { sourceColumnType: x.type, sourceColumnName: `${x.tableSchema}.${x.tableName}.${x.name}` }
  switch(decideDataTypeMaskingKind(x.type)) {
    case 'time':
    case 'not_support': { return }
    case 'date': { 
      return {
        inferredType: { 
          type: 'date', 
          length: figureOutDateRank(x.type),
          ...srcColumnType 
        },
        dataType: ObfuscationDataType.DateTime,
      }
    }
    case 'string': {
      return {
        inferredType:{
          type: 'string',
          length: x.maxLength || 0,
          ...srcColumnType,
        },
        dataType: ObfuscationDataType.String,
      }
    }
    case 'integer_num': {
      return {
        inferredType:{  
          type: 'integer',
          length: getNumericTypeStorageLength(x) || 0,
          ...srcColumnType,
        },
        dataType: ObfuscationDataType.Integer,
      }
    }
    case 'non_integer_num': {
      return {
        inferredType: {
          type: 'numeric',
          p: x.precision || 0,
          s: x.scale || 0,
          pOra: x.precision || 0,
          sOra: x.scale || 0,
          pn: x.precision || 0,
          ...srcColumnType,
        },
        dataType: ObfuscationDataType.Numeric,
      }
    }
  }
}


const integerRanks = [BigInt(255), BigInt(32767), BigInt(8388607), BigInt(2147483647)]
const ranksToStorageLength = [8, 1, 2, 3, 4]

export const inferDataTypeInfo = (dataType: ObfuscationDataType, valuesList: string[]): Inferred|undefined => {
  switch(dataType) {
    case ObfuscationDataType.DateTime: { 
      const ds = valuesList.flatMap(x => {
        if (!x) { return [] }
        const n = Date.parse(x)
        return isNaN(n) ? [] : [n]
      })
      const [d_min, d_max] = [new Date(Math.min(...ds)), new Date(Math.max(...ds))].map(d => new Date(d.getFullYear(), d.getMonth(), d.getDate()))
      
      return { 
        type: 'date',
        length: Math.min(3, ...Object.values(dateKinds)
          .filter(x => x.min<=d_min && d_max<=x.max).map(x => x.rank))
      } 
    }
    case ObfuscationDataType.String: {
      return {
        type: 'string',
        length: Math.max(1, ...valuesList.map(s => s.length)),
      }
    }
    case ObfuscationDataType.Integer: {
      if (valuesList.length === 0) { return }
  
      const maxN = valuesList.map(s => 
        s
        ? ({ v: BigInt(s.replace('-','')), negative: s.startsWith('-') })
        : { v: BigInt(0), negative: false }
      ).reduce((a, b) => {
        return a.v > b.v ? a : b
      })
      const rank = integerRanks.reduce((acc, x, i) => {
        return acc || (
          x + (maxN.negative ? BigInt(1) : BigInt(0)) >= maxN.v
          ? (i + 1) : 0)
      }, 0)
  
      return {
        type: 'integer',
        length: ranksToStorageLength[rank] || 8
      }
    }
    case ObfuscationDataType.Numeric: {
      if (valuesList.length === 0 
      || (valuesList.length === 1 && !valuesList[0])
      ) { return }
  
      return {
        type: 'numeric',
        ...valuesList.flatMap(calc_p_s_n).reduce(merge_p_s_n),
        ...valuesList.flatMap(calc_ora_p_s).reduce(merge_ora_p_s),
      }
    }

  }
}


interface p_s_n { p: number, s: number, pn: number }
interface ora_p_s { pOra: number, sOra: number }

const calc_p_s_n = (x:string):p_s_n[] => {
  if (!x) { return [] }

  const signTrimmed = x.replace(/[+-]/, '')
  const ctx = { head:true, dot_found:false, p:0, s:0 }
  for(var c of signTrimmed) {
    if (ctx.head) {
      if (c === '0') { continue; }
      ctx.head = false
      ctx.p = ctx.p + 1
      if (c === '.') { ctx.dot_found = true }
    }
    else {
      if (c === '.') { ctx.dot_found = true; continue; }
      ctx.p = ctx.p + 1
      if (ctx.dot_found) { ctx.s = ctx.s + 1 }
    }
  }
  const tailTrimmed = signTrimmed.replace(/0+$/, '')
  const fix = ctx.dot_found ? signTrimmed.length - tailTrimmed.length : 0

  const pn = parseFloat(!ctx.dot_found ? signTrimmed : tailTrimmed.replace('.', '')).toString(2).length
  return [{
    s: ctx.s - fix,
    p: ctx.p - fix,
    pn,
  }]
}

const merge_p_s_n = (x: p_s_n, y: p_s_n):p_s_n => ({
  p: Math.max((x.p - x.s), (y.p - y.s)) + Math.max(x.s, y.s),
  s: Math.max(x.s, y.s),
  pn: Math.max(x.pn, y.pn),
})

const calc_ora_p_s = (x:string):ora_p_s[] => {
  if (!x) { return [] }

  const x1 = x.replace(/[+-]/, '')
  const ctx = { head:true, dot_found:false, m:0, e:0 }
  for(var c of x1) {
    if (ctx.head) {
      if (c === '.') { ctx.dot_found = true }
      else {
        if (c !== '0') { ctx.head = false; ctx.m = ctx.m + 1 }
        if (ctx.dot_found) { ctx.e = ctx.e - 1 }
      }
      continue;
    }
    else {
      if (c === '.') { ctx.dot_found = true }
      else {
        ctx.m = ctx.m + 1
        if (!ctx.dot_found) { ctx.e = ctx.e + 1 }
      }
    }
  }
  if (ctx.m === 0) {
    ctx.m = 1
  } else {
    const fix = x1.length - x1.replace(/0+$/, '').length
    ctx.m = ctx.m - fix
  }

  return [{
    pOra: ctx.m,
    sOra: ctx.m - 1 - ctx.e,
  }]
}

export const merge_ora_p_s = (x:ora_p_s, y:ora_p_s):ora_p_s => {
  const [is_xs_min, sOra_max] = [Math.min(x.sOra, y.sOra) === x.sOra, Math.max(x.sOra, y.sOra)]
  const k = x.pOra === y.pOra ? Infinity : ((y.sOra - x.sOra) / (y.pOra - x.pOra))
  if (k > 1 || k < 0) {
    return {
      pOra: (is_xs_min ? x.pOra : y.pOra) + Math.abs(x.sOra - y.sOra),
      sOra: sOra_max,
    }
  } else {
    return {
      pOra: Math.max(x.pOra, y.pOra),
      sOra: sOra_max,
    }
  }
}

export const displayInferredType = (x: Inferred|null) => {
  if (!x) { return null }
  const strArr = [] as string[]
  switch(x.type) {
    case 'string': { strArr.push(`varchar(${x.length})`); break; }
    case 'integer': {
      strArr.push(x.length === 1 ? 'tinyint'
        : x.length === 2 ? 'smallint'
        : x.length <= 4 ? 'int'
        : 'bigint'
      );
      break;
    }
    case 'numeric': {
      if (x.pn < 25) {
        strArr.push(`float(${x.pn})`, 'real', `decimal(${x.p}, ${x.s})`)
      } else if (x.pn < 54) {
        strArr.push(`float(${x.pn})`, 'double', `decimal(${x.p}, ${x.s})`)
      } else {
        strArr.push(`decimal(${x.p}, ${x.s})`)
      }
      break;
    }
    case 'date': {
      strArr.push(...(
        x.length === 1 ? ['smalldatetime']
      : x.length === 2 ? ['datetime']
      : ['datetime2', 'datetimeoffset', 'timestamp']))
      break;
    }
    default: { return null }
  }
  return (
    <Typography variant="caption" sx={{ mr: 4 }}>
      Inferred Type: {
        strArr.map((s, i) => 
          <Fragment key={s}>{i !== 0 && ', '}<b>{s}</b></Fragment>)
      }
    </Typography>
  )
}
